import React, { useState, useEffect } from 'react';
import {
    Wifi, Car, Coffee, Tv, Wind, Utensils, MapPin, Smile, Star, Key, ShieldCheck,
    HelpCircle, Loader2, Info, Zap, UserCheck
} from 'lucide-react';
import api from '../utils/api';
import { SiteSettings } from '../types';

// Icon Map (Consistent with Admin Dashboard)
const ICON_MAP: Record<string, React.ElementType> = {
    'Wifi': Wifi,
    'Car': Car,
    'Coffee': Coffee,
    'Tv': Tv,
    'Wind': Wind,
    'Utensils': Utensils,
    'MapPin': MapPin,
    'Smile': Smile,
    'Star': Star,
    'Key': Key,
    'ShieldCheck': ShieldCheck
};

interface ServiceItem {
    id: string;
    iconName: string;
    title: string;
    description: string;
}

const RatesContent = () => {
    const [settings, setSettings] = useState<SiteSettings>({});
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await api.get('/content/settings');
                if (response.data.success && response.data.data) {
                    const data = response.data.data;
                    setSettings(data);

                    // Parse dynamic services list
                    if (data.services_list) {
                        try {
                            const parsedServices = JSON.parse(data.services_list);
                            if (Array.isArray(parsedServices)) {
                                setServices(parsedServices);
                            }
                        } catch (e) {
                            console.error("Error parsing services JSON", e);
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching services content", error);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, []);

    // Helper to render the correct icon
    const renderIcon = (name: string) => {
        const IconComponent = ICON_MAP[name] || Star; // Fallback to Star if not found
        return <IconComponent className="w-8 h-8" />;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-20">
                <Loader2 className="w-10 h-10 text-brand animate-spin" />
            </div>
        );
    }

    return (
        <div className="pt-36 px-4 md:px-8 max-w-7xl mx-auto animate-fade-in pb-20">

            {/* --- SECTION HEADER (Dynamic) --- */}
            <div className="text-center mb-16">
                <h1 className="text-5xl font-serif font-bold text-primary mb-6">
                    {settings.services_title || "Services & Rates"}
                </h1>
                <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
                    {settings.services_text || "Transparency and quality. The standard for every Another Home stay."}
                </p>
            </div>

            {/* --- DYNAMIC SERVICES GRID --- */}
            {services.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {services.map((service) => (
                        <div
                            key={service.id}
                            className="bg-white border border-gray-100 rounded-[2rem] p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                        >
                            <div className="bg-brand/5 w-16 h-16 rounded-2xl flex items-center justify-center text-brand mb-6 group-hover:bg-brand group-hover:text-white transition-colors duration-300">
                                {renderIcon(service.iconName)}
                            </div>
                            <h3 className="text-2xl font-bold mb-3 text-primary font-serif">
                                {service.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed text-base">
                                {service.description}
                            </p>
                        </div>
                    ))}
                </div>
            ) : (
                // Elegant fallback
                <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-[2rem] border-2 border-dashed border-gray-200">
                    <Info className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">No featured services listed at the moment.</p>
                </div>
            )}

            {/* --- INCLUDED IN EVERY STAY (Static) --- */}
            <div className="mt-24 border-t border-gray-100 pt-16">
                <div className="text-center mb-12">
                    <span className="text-brand font-bold text-sm tracking-widest uppercase">Included</span>
                    <h2 className="text-3xl font-bold text-primary mt-2 font-serif">Standard Inclusions</h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Item 1 */}
                    <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl">
                        <Key className="w-10 h-10 text-brand mb-4" />
                        <h4 className="font-bold text-lg text-primary mb-2">Digital Check-in</h4>
                        <p className="text-gray-500 text-sm">24/7 autonomous access via personal code sent by email.</p>
                    </div>

                    {/* Item 2 */}
                    <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl">
                        <ShieldCheck className="w-10 h-10 text-brand mb-4" />
                        <h4 className="font-bold text-lg text-primary mb-2">Pro Cleaning</h4>
                        <p className="text-gray-500 text-sm">Certified professional sanitization before every arrival.</p>
                    </div>

                    {/* Item 3 */}
                    <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl">
                        <Zap className="w-10 h-10 text-brand mb-4" />
                        <h4 className="font-bold text-lg text-primary mb-2">Premium Linens</h4>
                        <p className="text-gray-500 text-sm">High-quality fresh towels and bedding provided.</p>
                    </div>

                    {/* Item 4 */}
                    <div className="flex flex-col items-center text-center p-6 bg-gray-50 rounded-2xl">
                        <UserCheck className="w-10 h-10 text-brand mb-4" />
                        <h4 className="font-bold text-lg text-primary mb-2">Guest Support</h4>
                        <p className="text-gray-500 text-sm">Dedicated team available to assist during your stay.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RatesContent;